import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { Permission } from '../../permissions/roles.js';
import { UserEmployeeDetailsActions } from '../../users/employees/UserEmployeeDetailsPolicy.js';
import { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import { employmentPermitListRouter } from '../employment-permits-router.js';
import { EmploymentPermitsAPI } from '../EmploymentPermitsAPI.js';
import { BeschaeftigungsGenehmigung, exmploymentPermitToString } from '../entities/beschaeftigungs-genehmigung.js';
import { SGEmploymentPermitStatus } from '../mail/sg-employment-permit-status.js';

const loadingSkeleton = html`
  <div class="flex flex-col gap-5 p-6 w-full">
    ${repeat(
      Array.from({ length: 2 }),
      (_, i) => i,
      i => html`
        <div class="card p-5">
          <div class="skeleton h-8 w-full"></div>
        </div>
      `,
    )}
  </div>
`;

const permitCard = stateful(
  (props: {
    permit: NarrowedEntity<BeschaeftigungsGenehmigung, { _id: 1; beantragtVon: 1; befristetBis: 1 }>;
    active: boolean;
  }) => {
    return html`
      <div
        class="card flex flex-row ${
          props.active ? 'bg-primary text-primary-content' : 'text-base-content'
        } p-4 justify-between select-none cursor-pointer items-center gap-8"
        @click=${() => {
          employmentPermitListRouter.goTo('details', { params: { permitID: props.permit._id } });
        }}
      >
        <span class="font-semibold md:text-lg"> ${exmploymentPermitToString(props.permit)} </span>

        <button
          type="button"
          class="btn btn-square btn-ghost btn-sm tooltip hover:text-error"
          aria-label="Löschen"
          data-tip="Löschen"
          ${hoverTooltip()}
          @click=${async (e: Event) => {
            e.stopPropagation();
            e.preventDefault();

            const confirm = await confirmDialog(
              `Sind Sie sicher, dass Sie diese Beschäftigungsgenehmigung löschen möchten?`,
            );
            if (!confirm) return;

            await guardDialog(() => props.permit.remove(), {
              success: () => {
                void XSnackbar.show('Beschäftigungsgenehmigung wurde erfolgreich gelöscht.');
                if (props.active) employmentPermitListRouter.goTo('root');
              },
            });
          }}
        >
          <span class="icon-[material-symbols--delete-rounded] size-5"></span>
        </button>
      </div>
    `;
  },
);

const NewEmploymentPermitButton = stateful((props: { userID: Maybe<string> }) => {
  return html`
    <button
      type="button"
      class="btn btn-dash btn-block min-w-[280px]"
      @click=${async () => {
        await guardDialog(
          async () => {
            const permitID = await new BeschaeftigungsGenehmigung({ userID: props.userID }).create();
            employmentPermitListRouter.goTo('details', { params: { permitID } });
          },
          { success: () => XSnackbar.show('Beschäftigungsgenehmigung wurde erfolgreich erstellt.') },
        );
      }}
    >
      <span class="icon-[material-symbols--add-rounded] size-5"></span>
      Beschäftigungsgenehmigung hinzufügen
    </button>
  `;
});

const beschaeftigungsgenehmigungenEmptyPlaceholder = stateful((props: { userID: Maybe<string> }) => {
  return html`
    <div class="flex flex-col gap-4 items-center justify-center m-16">
      ${EmptyState({ title: 'Dieser Mitarbeiter hat noch keine Beschäftigungsgenehmigungen.' })}
    </div>
  `;
});

export const SGEmploymentPermitList = stateful((props: { userID: Maybe<string>; activePermitID: Maybe<string> }) => {
  const user = useObservable(() => currentUser<Person>(), []);

  if (!user?.hasPermission(Permission.AG_SEKTION_R))
    return html`
      <div class="card flex flex-grow p-4 m-4 items-center justify-center">
        ${EmptyState({ title: 'Sie haben nicht die Berechtigung diese Seite zu sehen' })}
      </div>
    `;

  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.userID,
      )({
        _id: 1,
        _class: 1,
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        debitorennummer: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        mitarbeiterDaten: { anDokumente: { beschaeftigungsgenehmigungIDs: 1 } },
      }),
    [props.userID],
  );

  const { value: employmentPermits, loading } = useQuery(
    () =>
      props.userID
        ? EmploymentPermitsAPI.subscribeForUser(props.userID)(selectionSet(() => BeschaeftigungsGenehmigung, 1))
        : of(null),
    [props.userID],
  );

  const { can: canViewRecruitmentDocuments } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_RECRUITMENT_DOCUMENTS,
    viewedUser: person,
  });

  const { can: canEditSections } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS,
    viewedUser: person,
  });

  if (loading || personLoading || !person) return loadingSkeleton;

  const [personObject] = useState(person.toObject(), [person._id]);
  const form = useForm(personObject, { validator: null }, []);

  const handleSave = async () => {
    await guardDialog(
      async () => {
        await form.uploadAllPendingFiles();
        const value = constructValue(form.document) as Person;
        await value.save();
      },
      { successText: 'Person gespeichert' },
    );
  };

  return html`
    <div class="flex flex-col gap-4 m-4">
      ${SGEmploymentPermitStatus({ userID: props.userID, existingPermits: !!employmentPermits?.length })}
      ${
        canViewRecruitmentDocuments
          ? html`
              <div class="card card-body">
                ${CardHeader({ title: 'Dokumente' })}
                <div class="flex gap-2">
                  ${FileInput({
                    ...form.field('mitarbeiterDaten', 'anDokumente', 'beschaeftigungsgenehmigungIDs'),
                    placeholder: 'Keine Beschäftigungsgenehmigungen hinterlegt',
                    placeholderMode: 'hide',
                    multi: true,
                  })}
                  ${
                    canEditSections
                      ? html`
                          <button class="btn btn-success" @click=${handleSave}>
                            <span class="icon-[material-symbols--save] text-success-content"></span>
                          </button>
                        `
                      : nothing
                  }
                </div>
              </div>
            `
          : nothing
      }
      ${!employmentPermits?.length ? beschaeftigungsgenehmigungenEmptyPlaceholder(props) : nothing}
      ${repeat(
        employmentPermits ?? [],
        permit => permit._id,
        permit => permitCard({ permit, active: props.activePermitID === permit._id }),
      )}
      ${NewEmploymentPermitButton(props)}
    </div>
  `;
});
